import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { construirRutaDeAlta } from '../../../nucleo/navegacion/contextoDeNavegacion.js';
import { consultarMensualidades } from '../../msi/api/msiApi.js';
import { CLASES, consultarCuentas } from '../api/cuentasApi.js';
import { PestanasDeCuentas } from '../componentes/PestanasDeCuentas.jsx';
import {
  indexarTotalesPorTarjeta, MARCAS_DE_MESES, resolverMarcaDeMeses,
} from '../logica/marcaDeMesesEnElRenglon.js';
import { agruparPorMoneda, importe, iniciales, NOMBRE_DE_MONEDA, vigencia, vigenciaVencida } from '../formato.js';

/*
 * HERENCIA Y REGRESO DE LA PESTAÑA (RF-145, RF-146, CR-01). En Cuentas la causa
 * del defecto era la opuesta a la del historial y es concreta: la pestaña YA es
 * direccionable y el botón de alta YA manda su contexto (`?credito=1`); lo que
 * estaba mal era EL REGRESO —el alta salía a `/cuentas` fijo, así que quien
 * registraba una tarjeta desde la pestaña Tarjetas aterrizaba en la pestaña
 * Cuentas—. Por eso aquí solo se agrega a dónde hay que volver.
 */
const RUTA_DE_ALTA_DE_TARJETA = construirRutaDeAlta('/cuentas/nueva', {
  contextoDeRegreso: '/cuentas/tarjetas',
  sugerencias: { credito: '1' },
});

/**
 * Lista de tarjetas — pestaña "Tarjetas".
 *
 * Una tarjeta de crédito ES una cuenta de tipo Crédito, no una entidad aparte
 * (D-18): esta pantalla es la MISMA consulta con clase=CREDITO.
 *
 * Deuda y disponible se CALCULAN (D-24, RN-057). El disponible se muestra en
 * CERO, nunca negativo, cuando hay sobregiro, y no bloquea nada. La deuda NO se
 * resta del saldo de las cuentas (S-21) y las monedas nunca se combinan (RN-062).
 */
export default function ListaDeTarjetas() {
  const hoy = obtenerFechaDeNegocioHoy();

  // Cuatro estados excluyentes, igual que en Cuentas (RF-087, RN-140, RN-141): el
  // bloque de "todavía no registras tarjetas" NO se pinta antes de la respuesta.
  const consultar = useCallback(() => consultarCuentas({ clase: CLASES.credito }), []);
  const listado = useConsultaDeListado(consultar, (respuesta) => (respuesta?.cuentas ?? []).length === 0);
  const { datos, estado } = listado;

  const tarjetas = datos?.cuentas ?? [];
  const bloques = agruparPorMoneda(tarjetas);
  const totalDe = (idMoneda) => (datos?.totalesPorMoneda ?? []).find((t) => t.idMoneda === idMoneda);

  /*
   * F-05 · ENGANCHE CON EL 4/6 EN UNA SOLA LLAMADA (RF-183, RNF-103, RN-554).
   *
   * `ConsultarMensualidadesDePeriodo` SIN `idCuenta` devuelve `totalesPorTarjeta` de
   * TODAS las tarjetas de una vez; esa respuesta se reparte por `idCuenta` entre los
   * renglones ya pintados. PROHIBIDA una llamada por tarjeta: una por pestaña, con
   * tres tarjetas o con cuarenta.
   *
   * Y NO ES CONTRATO NUEVO DEL 4/6: la operación ya existe, el detalle de la tarjeta
   * ya la consume, y aquí solo se declara un consumidor más. El 4/6 no cambia una
   * línea.
   *
   * SI EL 4/6 NO RESPONDE, la lista se pinta COMPLETA sin el dato y SIN MOSTRAR
   * ERROR: el enganche es informativo y la lista no depende de él (RN-556). El mapa
   * se queda vacío y ninguna tarjeta muestra marca.
   */
  const [totalesDeMeses, setTotalesDeMeses] = useState(null);

  useEffect(() => {
    let vigente = true;
    consultarMensualidades({ fechaLocal: hoy })
      .then((respuesta) => vigente && setTotalesDeMeses(respuesta?.totalesPorTarjeta ?? []))
      .catch(() => vigente && setTotalesDeMeses([]));
    return () => { vigente = false; };
  }, [hoy]);

  const mapaDeMeses = useMemo(() => indexarTotalesPorTarjeta(totalesDeMeses), [totalesDeMeses]);

  return (
    <Shell titulo="Cuentas y tarjetas" subtitulo="Deuda y disponible calculados">
      <PestanasDeCuentas activa="tarjetas" />

      <ListadoActualizando visible={listado.actualizando} />

      {estado === ESTADO_DE_LISTADO.CARGANDO && <ListadoCargando renglones={3} texto="Cargando tus tarjetas…" />}

      {estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={listado.recargar} />
      )}

      {estado === ESTADO_DE_LISTADO.VACIO ? (
        <div className="card" style={{ margin: 12 }}>
          <div style={{ textAlign: 'center', padding: '22px 16px' }}>
            <p><b>Todavía no registras tarjetas de crédito.</b></p>
            <p>
              Al registrarla te pedirá el <b>límite</b>, el <b>día de corte</b> y el
              <b> día límite de pago</b>, para que esas fechas dejen de llevarse de memoria.
            </p>
            <Link className="btn" to={RUTA_DE_ALTA_DE_TARJETA}>Registrar una tarjeta</Link>
          </div>
        </div>
      ) : estado === ESTADO_DE_LISTADO.CON_DATOS ? (
        bloques.map((bloque) => (
          <div key={bloque.idMoneda} style={{ margin: '12px 12px 0' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>
              {(NOMBRE_DE_MONEDA[bloque.codigoISO] ?? bloque.codigoISO).toUpperCase()} · {bloque.codigoISO}
            </h4>

            {/* RN-624 · FIX del 2026-10-08. Tenía `?? '0.00'`: un total que no
                llegó se pintaba como deuda cero, que es justo lo que la regla
                prohíbe. Es la MISMA cifra `totalDeudas` que presenta la pestaña
                de Cuentas, del mismo contrato (RN-625, CA-14-31). */}
            <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              {/* D-477 (2026-10-18) · El rótulo dice «Deuda de tarjetas», el
                  MISMO que la pantalla de Cuentas, para que se vea de un vistazo
                  que las dos cifras son la misma cosa (RN-799). Cambia el texto y
                  nada más: el campo que lo alimenta sigue siendo `totalDeudas`,
                  que desde la rev. 12 del contrato suma solo tipo CRÉDITO. */}
              <span data-prueba="rotulo-de-la-deuda">Deuda de tarjetas en {bloque.codigoISO}</span>
              <b style={{ fontSize: 19 }} data-prueba="deuda-total-de-la-moneda" data-moneda={bloque.codigoISO}>
                {totalDe(bloque.idMoneda)
                  ? importe(totalDe(bloque.idMoneda).totalDeudas, bloque.codigoISO)
                  : 'No disponible'}
              </b>
            </div>

            <div className="ayuda" style={{ margin: '-4px 2px 8px' }}>
              La deuda <b>no se resta</b> del saldo de tus cuentas: un gasto con tarjeta aumenta la
              deuda y no baja ningún saldo de banco (S-21).
            </div>

            {bloque.cuentas.map((tarjeta) => {
              const meses = resolverMarcaDeMeses(tarjeta, mapaDeMeses.get(tarjeta.idCuenta));
              return (
              <Link key={tarjeta.idCuenta} className="card" to={`/cuentas/${tarjeta.idCuenta}`}
                style={{ display: 'block', textDecoration: 'none', color: 'inherit', marginBottom: 10, padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="punto" aria-hidden="true">{iniciales(tarjeta.nombre)}</div>
                  <div className="crece">
                    <b>{tarjeta.nombre}</b>
                    <small>
                      {tarjeta.nombreTipoCuenta} · {tarjeta.esPropia ? 'Tuya' : `de ${tarjeta.nombrePropietario}`}
                      {tarjeta.cuatroUltimosDigitos && ` · ••${tarjeta.cuatroUltimosDigitos}`}
                      {vigencia(tarjeta.vigenciaMes, tarjeta.vigenciaAnio) && (
                        vigenciaVencida(tarjeta.vigenciaMes, tarjeta.vigenciaAnio, hoy)
                          ? ` · vigencia ${vigencia(tarjeta.vigenciaMes, tarjeta.vigenciaAnio)} vencida`
                          : ` · vence ${vigencia(tarjeta.vigenciaMes, tarjeta.vigenciaAnio)}`
                      )}
                    </small>
                  </div>
                  <span className="imp">{importe(tarjeta.deuda ?? tarjeta.saldoInicial, tarjeta.codigoISO)}</span>
                </div>

                <div className="ayuda" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 8 }}>
                  <span>Límite <b>{importe(tarjeta.limiteCredito, tarjeta.codigoISO)}</b></span>
                  <span>
                    Disponible <b>{importe(tarjeta.disponible, tarjeta.codigoISO)}</b>
                    {tarjeta.haySobregiro && <span className="chip"> ▲ Sobregiro</span>}
                  </span>
                  {/* La lista muestra día de corte y día límite de pago, NO días de
                      gracia: cambian mes a mes y solo ConsultarFechasDeTarjeta los
                      da exactos (D-31, RN-056c). */}
                  <span>Corte <b>día {tarjeta.diaCorte}</b></span>
                  <span>Pagas <b>día {tarjeta.diaLimitePago}</b></span>

                  {/* F-05 · RF-184, RN-555, RN-557 · DENTRO del mismo bloque de ayuda
                      donde ya viven Límite, Disponible, Corte y Pagas, como un
                      apartado más. Dos cosas y solo dos: la marca discreta de que la
                      tarjeta tiene planes a meses vigentes, y el importe que cae en el
                      corte próximo.

                      LA CIFRA ES UN REPARTO DE DEUDA YA CONTADA, NO DEUDA ADICIONAL
                      (D-53, RN-103c): no se suma a la deuda del renglón, no se suma al
                      total por moneda y no se resta del disponible. Si al leer el
                      renglón alguien puede entender que la tarjeta debe la deuda MÁS
                      los meses, el renglón está mal.

                      LA MARCA NO ES UN SEGUNDO ENLACE (RN-557.3): el renglón completo
                      ya lleva al detalle, que es donde está el desglose. Un segundo
                      destino dentro del mismo renglón sería un blanco de toque de
                      44 px dentro de otro.

                      Lo que NO entra aquí: el pendiente de cortes futuros, el número
                      de planes y el desglose plan por plan. Eso es del detalle. */}
                  {meses.marca !== MARCAS_DE_MESES.SIN_MARCA && (
                    <span data-prueba="marca-de-meses">
                      <span className="chip" aria-hidden="true">A MESES</span>{' '}
                      {meses.marca === MARCAS_DE_MESES.NADA_EN_ESTE_CORTE
                        ? meses.texto
                        : <>A meses este corte <b data-prueba="importe-a-meses">{meses.cifra}</b></>}
                    </span>
                  )}

                  {tarjeta.puedeDesactivarse && <span className="chip">✔ En cero · se puede desactivar</span>}
                </div>
              </Link>
              );
            })}
          </div>
        ))
      ) : null}

      <div className="aviso i">
        La <b>vigencia es informativa</b>: que esté vencida no caduca la cuenta, no la desactiva, no
        bloquea nada y no genera aviso automático. Solo dice que ya tocó
        <b> renovar el plástico</b>, que es una operación aparte y conserva saldo e historial.
        El disponible se muestra en <b>cero, nunca negativo</b>, cuando hay sobregiro, y las
        fechas de corte <b>se recorren al último día</b> en los meses cortos.
      </div>

      {/* RF-141, RN-355 · Aquí vivía un aviso que anunciaba los meses sin intereses
          como función futura y cerraba diciendo que ahí no se construía nada de ello.
          Se retiró COMPLETO Y SIN SUSTITUTO: la función ya existe y se llega a ella
          abriendo la tarjeta. Tampoco se puso un anuncio en su lugar, porque no hay
          nada que anunciar.
          RN-356 · La pregunta abierta que el 08 dejó aquí —«el 08 no agrega marca
          alguna de plan a meses abierto en el renglón de la tarjeta… queda como
          pregunta abierta»— quedó CERRADA el 2026-10-05 con la decisión D-295: el
          renglón lleva marca e importe del próximo corte. Construido arriba, dentro
          del bloque de Límite/Disponible/Corte/Pagas (F-05, RF-184, RN-557). */}

      <div style={{ margin: '10px 12px' }}>
        <Link className="btn ancho" to={RUTA_DE_ALTA_DE_TARJETA}>Nueva tarjeta de crédito</Link>
      </div>
    </Shell>
  );
}
